import React, { useState } from 'react';

interface Tag {
  id: string;
  name: string;
  color?: string;
  usage_count?: number;
}

interface TagFilterProps {
  tags: Tag[];
  selectedTags: string[];
  onSelectTag: (tagId: string) => void;
  onCreateTag?: () => void;
  loading?: boolean;
  viewMode?: 'cloud' | 'list';
}

export const TagFilter: React.FC<TagFilterProps> = ({
  tags,
  selectedTags,
  onSelectTag,
  onCreateTag,
  loading = false,
  viewMode: initialViewMode = 'cloud',
}) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [viewMode, setViewMode] = useState<'cloud' | 'list'>(initialViewMode);

  // Generate color from tag name
  const getTagColor = (tagName: string, customColor?: string) => {
    if (customColor) return customColor;

    const colors = [
      '#3b82f6', '#8b5cf6', '#10b981', '#f59e0b', '#ef4444',
      '#06b6d4', '#ec4899', '#14b8a6', '#f97316', '#6366f1'
    ];

    let hash = 0;
    for (let i = 0; i < tagName.length; i++) {
      hash = tagName.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
  };

  // Sort tags by usage count for cloud view
  const sortedTags = [...tags].sort((a, b) =>
    (b.usage_count || 0) - (a.usage_count || 0)
  );

  // Calculate font size based on usage count for cloud view
  const getTagSize = (tag: Tag) => {
    if (!tag.usage_count) return 14;
    const maxCount = Math.max(...tags.map(t => t.usage_count || 0));
    const minSize = 12;
    const maxSize = 20;
    return minSize + ((tag.usage_count / maxCount) * (maxSize - minSize));
  };

  const renderTagCloud = () => (
    <div className="tag-cloud">
      {sortedTags.map(tag => {
        const isSelected = selectedTags.includes(tag.name);
        const fontSize = getTagSize(tag);
        const tagColor = getTagColor(tag.name, tag.color);

        return (
          <button
            key={tag.id}
            onClick={() => onSelectTag(tag.id)}
            className={`tag-cloud-item ${isSelected ? 'selected' : ''} animate-fade-in`}
            style={{
              fontSize: `${fontSize}px`,
              backgroundColor: isSelected ? tagColor : `${tagColor}15`,
              color: isSelected ? 'white' : tagColor,
              borderColor: tagColor,
            }}
            title={`${tag.name} (${tag.usage_count || 0})`}
          >
            {tag.name}
          </button>
        );
      })}
    </div>
  );

  const renderTagList = () => (
    <div className="tag-list">
      {tags.map(tag => {
        const isSelected = selectedTags.includes(tag.name);
        const tagColor = getTagColor(tag.name, tag.color);

        return (
          <button
            key={tag.id}
            onClick={() => onSelectTag(tag.id)}
            className={`tag-list-item ${isSelected ? 'selected' : ''} animate-slide-in`}
            style={{
              borderLeft: `4px solid ${tagColor}`,
              backgroundColor: isSelected ? `${tagColor}15` : 'transparent',
            }}
          >
            <span className="tag-name">{tag.name}</span>
            {tag.usage_count !== undefined && (
              <span className="tag-count" style={{
                backgroundColor: isSelected ? tagColor : undefined,
              }}>{tag.usage_count}</span>
            )}
          </button>
        );
      })}
    </div>
  );

  return (
    <div className="tag-filter">
      <div className="tag-header">
        <button
          className="tag-toggle"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <span className="toggle-icon">{isExpanded ? '▼' : '▶'}</span>
          <span className="gradient-text tag-title">标签</span>
        </button>
        <div className="tag-actions">
          <button
            className="tag-view-toggle"
            onClick={() => setViewMode(viewMode === 'cloud' ? 'list' : 'cloud')}
            title={viewMode === 'cloud' ? '列表视图' : '云视图'}
          >
            {viewMode === 'cloud' ? '☰' : '☁'}
          </button>
          {onCreateTag && (
            <button
              className="tag-create-btn"
              onClick={onCreateTag}
              title="创建新标签"
            >
              <span>+</span>
            </button>
          )}
        </div>
      </div>

      {isExpanded && (
        <div className="tag-content">
          {loading ? (
            <div className="tag-loading">
              <div className="spinner"></div>
            </div>
          ) : tags.length === 0 ? (
            <div className="tag-empty">
              <span className="empty-icon">🏷️</span>
              <p>暂无标签</p>
            </div>
          ) : (
            viewMode === 'cloud' ? renderTagCloud() : renderTagList()
          )}
        </div>
      )}

      <style>{`
        .tag-filter {
          background: var(--color-bg-primary);
          border-radius: var(--radius-lg);
          padding: var(--spacing-lg);
          box-shadow: var(--shadow-md);
          transition: all var(--transition-base);
        }

        .tag-filter:hover {
          box-shadow: var(--shadow-lg);
        }

        .tag-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: var(--spacing-md);
          padding-bottom: var(--spacing-sm);
          border-bottom: 1px solid var(--color-gray-200);
        }

        .tag-toggle {
          display: flex;
          align-items: center;
          gap: var(--spacing-sm);
          background: none;
          border: none;
          font-size: 16px;
          font-weight: 600;
          cursor: pointer;
          padding: var(--spacing-xs);
          color: var(--color-text-primary);
          transition: all var(--transition-base);
        }

        .tag-toggle:hover .toggle-icon {
          transform: scale(1.2);
        }

        .toggle-icon {
          transition: transform var(--transition-base);
          font-size: 12px;
        }

        .tag-title {
          font-size: 1.1rem;
          font-weight: 700;
        }

        .tag-actions {
          display: flex;
          gap: var(--spacing-sm);
        }

        .tag-view-toggle {
          width: 32px;
          height: 32px;
          border-radius: var(--radius-md);
          border: 2px solid var(--color-gray-200);
          background: var(--color-bg-primary);
          font-size: 16px;
          cursor: pointer;
          color: var(--color-text-secondary);
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all var(--transition-base);
        }

        .tag-view-toggle:hover {
          border-color: var(--color-primary);
          color: var(--color-primary);
          background: var(--color-primary-light);
          transform: scale(1.05);
        }

        .tag-create-btn {
          width: 32px;
          height: 32px;
          border-radius: 50%;
          border: 2px dashed var(--color-gray-300);
          background: var(--color-bg-primary);
          font-size: 20px;
          cursor: pointer;
          color: var(--color-gray-500);
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all var(--transition-base);
        }

        .tag-create-btn:hover {
          border-color: var(--color-primary);
          color: var(--color-primary);
          background: var(--color-primary-light);
          transform: rotate(90deg);
        }

        .tag-content {
          min-height: 60px;
        }

        /* Cloud View */
        .tag-cloud {
          display: flex;
          flex-wrap: wrap;
          gap: var(--spacing-sm);
          align-items: center;
        }

        .tag-cloud-item {
          padding: 6px 16px;
          border: 2px solid;
          border-radius: var(--radius-full);
          background: white;
          cursor: pointer;
          transition: all var(--transition-base);
          font-weight: 600;
          box-shadow: var(--shadow-sm);
        }

        .tag-cloud-item:hover {
          transform: scale(1.1) translateY(-2px);
          box-shadow: var(--shadow-md);
        }

        .tag-cloud-item.selected {
          font-weight: 700;
          box-shadow: var(--shadow-md);
          transform: scale(1.05);
        }

        /* List View */
        .tag-list {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .tag-list-item {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: var(--spacing-sm);
          padding: var(--spacing-sm) var(--spacing-md);
          border: none;
          background: none;
          border-radius: var(--radius-md);
          cursor: pointer;
          text-align: left;
          width: 100%;
          transition: all var(--transition-base);
          position: relative;
        }

        .tag-list-item:hover {
          transform: translateX(4px);
        }

        .tag-list-item.selected {
          font-weight: 600;
        }

        .tag-name {
          flex: 1;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          font-weight: 500;
        }

        .tag-count {
          font-size: 12px;
          font-weight: 600;
          color: white;
          background: var(--color-gray-400);
          padding: 3px 10px;
          border-radius: var(--radius-full);
          min-width: 28px;
          text-align: center;
          transition: all var(--transition-base);
        }

        .tag-loading {
          display: flex;
          justify-content: center;
          align-items: center;
          padding: var(--spacing-xl);
        }

        .tag-empty {
          text-align: center;
          padding: var(--spacing-xl);
          color: var(--color-text-secondary);
        }

        .empty-icon {
          font-size: 48px;
          display: block;
          margin-bottom: var(--spacing-sm);
          opacity: 0.5;
        }

        .tag-empty p {
          margin: 0;
          font-size: 14px;
        }

        @media (max-width: 768px) {
          .tag-filter {
            padding: var(--spacing-md);
          }

          .tag-cloud-item {
            font-size: 14px !important;
            padding: 4px 12px;
          }
        }
      `}</style>
    </div>
  );
};
